import { Box, Button, Paper, Skeleton, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useWidgetVariant } from "./useWidgetVariant";
import { useAppSelector } from "src/redux/hooks/redux";
import { selectCurrentCompany } from "src/redux/features/user/selectors/userState.selectors";
import { formatDate } from "src/utils";
import { ContextMenuTrigger } from "@components/ContextMenu";
import { buildTimelineContextMenu } from "./config/todayJobsCalendarContextMenu.config";
import { BaseModalComponent } from "@components/common/BaseModalComponent";
import { CompanyTimeCaption } from "@components/common/CompanyTime";
import { JobsCreate } from "./JobCreate/Jobs.layout";
import { VisitStepper } from "./JobVisits/job-visit-create/VisitStepper";
import { JobDetModal } from "../jobs/jobs-list/job-card/JobDetModal";
import { JobLayout } from "../jobs/jobs-list/jobs-layout/Job.layout";
import { useDispatch } from "react-redux";
import { clearLastCreatedJobId, resetJobCreateStepper } from "../../redux/features/jobCreateSlice";
import { useJobsAllQuery } from "../jobs/jobs-list/hooks/useJobsAllQuery";
import { clearTimelineClickedTime, getTimelineClickedTime, TIMELINE_CLICKED_TIME_KEY } from "./config/timeline.constants";
import { MoreJobsPopover } from "./MoreJobsPopover";
import { groupOverlappingJobs } from "./utils/groupOverlappingJobs";
import { formatCompanyInstant, isRepeatedCompanyInstant } from "./jobs-calendar/utils/companyWallTime";

// ─── Types ────────────────────────────────────────────────────────────────────

interface TodayJobsCalendarProps {
  currentDay: Date | Dayjs | null;
  filter?: string;
  jobs?: any[];
  isLoading?: boolean;
  disableDateFilter?: boolean;
  highlightedJobId?: string | number | null;
  compact?: boolean;
  allowMoreMenuInCompact?: boolean;
  moreMenuVariant?: "default" | "advanced";
  disableTimelineJobOpen?: boolean;
  disableTimelineScroll?: boolean;
  showCalendarInEventModal?: boolean;
  onJobSelect?: (job: any) => void;
}

export interface TodayJobsCalendarHandle {
  scrollToDate: (date: string | Date) => void;
}

interface EventLayout {
  left: number;
  width: number;
}

interface TimelineContextMenuContext {
  currentDay: Date;
  timeZone: string;
  filter: string;
  jobsCount: number;
  isTodayInTimeZone: boolean;
}

interface EventCardProps {
  job: any;
  jobEvent: {
    uid?: string | number;
    startDate: string | Date;
    endDate: string | Date;
  };
  index: number;
  timeZone: string;
  forceExpanded?: boolean;
  compact?: boolean;
  disableTransition?: boolean;
  disableJobOpen?: boolean;
  showCalendarInModal?: boolean;
  onJobSelect?: (job: any) => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const TIMELINE = {
  START_HOUR: 6,
  END_HOUR: 23,
  get TOTAL_HOURS() {
    return this.END_HOUR - this.START_HOUR;
  },
  get TOTAL_MINUTES() {
    return this.TOTAL_HOURS * 60;
  },
  EDGE_PADDING: 44,
  CARD_HEIGHT: 38,
  CALENDAR_HEIGHT: 88,
  STAGGER_Y: 4,
  MIN_SLOT_MINUTES: 30,
  EXPANDED_WIDTH_PX: 180,
  get MIN_WIDTH_PERCENT() {
    return (this.MIN_SLOT_MINUTES / this.TOTAL_MINUTES) * 100;
  },
} as const;

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useDragScroll() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    if (!scrollRef.current) return;

    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const onMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDragging || !scrollRef.current) return;

    e.preventDefault();

    const x = e.pageX - scrollRef.current.offsetLeft;
    scrollRef.current.scrollLeft = scrollLeft - (x - startX) * 1.5;
  };

  const onMouseUp = () => setIsDragging(false);
  const onMouseLeave = () => setIsDragging(false);

  return {
    scrollRef,
    isDragging,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getEventLayout(startDate: string | Date, endDate: string | Date, timeZone: string): EventLayout | null {
  const startHour = parseInt(formatDate(startDate, timeZone, "H") || "0", 10);
  const startMinute = parseInt(formatDate(startDate, timeZone, "m") || "0", 10);
  const endHour = parseInt(formatDate(endDate, timeZone, "H") || "0", 10);
  const endMinute = parseInt(formatDate(endDate, timeZone, "m") || "0", 10);

  const startMinutes = (startHour - TIMELINE.START_HOUR) * 60 + startMinute;
  const endMinutes = (endHour - TIMELINE.START_HOUR) * 60 + endMinute;

  if (endMinutes <= 0 || startMinutes >= TIMELINE.TOTAL_MINUTES) {
    return null;
  }

  const clampedStart = Math.max(0, startMinutes);
  const clampedEnd = Math.min(TIMELINE.TOTAL_MINUTES, endMinutes);

  return {
    left: (clampedStart / TIMELINE.TOTAL_MINUTES) * 100,
    width: Math.max(((clampedEnd - clampedStart) / TIMELINE.TOTAL_MINUTES) * 100, TIMELINE.MIN_WIDTH_PERCENT),
  };
}

function jobMatchesId(job: any, targetId?: string | number | null) {
  if (!targetId) return false;

  const target = String(targetId);
  const candidates = [job?.id, job?.jobId, job?.serviceOrderId, job?.eventUid, job?.uid];

  return candidates.some((candidate) => candidate !== undefined && candidate !== null && String(candidate) === target);
}

function buildHourMarkers(): number[] {
  const hours: number[] = [];

  for (let hour: number = TIMELINE.START_HOUR; hour <= TIMELINE.END_HOUR; hour += 2) {
    hours.push(hour);
  }

  if (!hours.includes(TIMELINE.END_HOUR)) {
    hours.push(TIMELINE.END_HOUR);
  }

  return hours;
}

const HOUR_MARKERS = buildHourMarkers();

// ─── Sub-components ───────────────────────────────────────────────────────────

function TimeGrid() {
  const ticks: { position: number; isHour: boolean; label?: string }[] = [];

  for (let hour: number = TIMELINE.START_HOUR; hour <= TIMELINE.END_HOUR; hour++) {
    const isMarkedHour = hour % 2 === 0 || hour === TIMELINE.END_HOUR;
    ticks.push({
      position: ((hour - TIMELINE.START_HOUR) / TIMELINE.TOTAL_HOURS) * 100,
      isHour: true,
      label: isMarkedHour ? `${hour}:00` : undefined,
    });

    // Add :15, :30, :45 ticks (skip the last hour to avoid overflow)
    if (hour < TIMELINE.END_HOUR) {
      [15, 30, 45].forEach((min) => {
        ticks.push({
          position: ((hour - TIMELINE.START_HOUR + min / 60) / TIMELINE.TOTAL_HOURS) * 100,
          isHour: false,
          label: min === 30 ? `${hour}:30` : undefined, // only label the :30
        });
      });
    }
  }

  return (
    <Box sx={{ position: "absolute", inset: 0 }}>
      {ticks.map(({ position, isHour, label }, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: `${position}%`,
            width: isHour ? "1px" : "1px",
            height: isHour ? "100%" : "40%", // quarter ticks are shorter
            bottom: 0, // anchor to bottom so they don't interfere with labels
            bgcolor: isHour ? "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.10)" : "rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.04)",
          }}
        >
          {label && (
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                top: isHour ? -20 : -16,
                transform: "translateX(-50%)",
                fontSize: isHour ? "0.65rem" : "0.58rem",
                color: isHour ? "var(--fx-surface-texttertiary, #94A3B8)" : "var(--fx-surface-texttertiary, #94A3B8)",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function NowIndicator({ currentMinutes }: { currentMinutes: number }) {
  const left = (currentMinutes / TIMELINE.TOTAL_MINUTES) * 100;

  return (
    <Box
      sx={{
        position: "absolute",
        left: `${left}%`,
        top: 0,
        bottom: 0,
        width: "2px",
        bgcolor: "#000",
        zIndex: 5,
        "&::after": {
          content: '""',
          position: "absolute",
          top: -3,
          left: -3,
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: "#000",
        },
      }}
    />
  );
}

function EventCard({
  job,
  jobEvent,
  index,
  timeZone,
  forceExpanded = false,
  compact = false,
  disableTransition = false,
  disableJobOpen = false,
  showCalendarInModal = true,
  onJobSelect,
}: EventCardProps) {
  const layout = getEventLayout(jobEvent.startDate, jobEvent.endDate, timeZone);

  if (!layout) return null;

  const isResidential = job.type === "residential";
  const staggerOffset = (index % 3) * TIMELINE.STAGGER_Y;
  const isExpanded = forceExpanded;
  const eventUid = jobEvent.uid || job.eventUid || job.uid || job.id;
  const jobId = job.jobId || job.serviceOrderId || job.id;
  const expandedWidth = compact ? 150 : TIMELINE.EXPANDED_WIDTH_PX;

  const eventCard = (
    <Box
      onMouseDown={(event) => event.stopPropagation()}
      onClick={disableJobOpen ? () => onJobSelect?.(job) : undefined}
      sx={{
        position: "absolute",
        left: `${layout.left}%`,
        width: isExpanded ? expandedWidth : `${TIMELINE.MIN_WIDTH_PERCENT}%`,
        minWidth: isExpanded ? expandedWidth : `${TIMELINE.MIN_WIDTH_PERCENT}%`,
        maxWidth: "none",
        top: `calc(50% + ${staggerOffset}px)`,
        height: TIMELINE.CARD_HEIGHT,
        transform: "translateY(-50%)",
        borderRadius: "4px",
        boxSizing: "border-box",
        px: 0.9,
        py: 0.35,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        overflow: "hidden",
        bgcolor: isExpanded ? (isResidential ? "#ffd79a" : "#9ed8f5") : isResidential ? "var(--fx-colors-warning-100, #FFEDD5)" : "#bae5fb",
        border: isExpanded ? `1px solid ${isResidential ? "rgba(255, 137, 18, 0.35)" : "rgba(33, 150, 243, 0.35)"}` : "1px solid rgba(0,0,0,0.08)",
        boxShadow: isExpanded ? "0px 8px 20px rgba(0,0,0,0.12)" : "0px 2px 4px rgba(0,0,0,0.04)",
        zIndex: isExpanded ? 20 : 2,
        cursor: disableJobOpen && onJobSelect ? "pointer" : disableJobOpen ? "default" : "pointer",
        transition: disableTransition ? "none" : "width 0.18s ease, min-width 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease",
        "&:hover": {
          width: expandedWidth,
          minWidth: expandedWidth,
          bgcolor: isResidential ? "#ffd79a" : "#9ed8f5",
          boxShadow: "0px 8px 20px rgba(0,0,0,0.12)",
          borderColor: isResidential ? "rgba(255, 137, 18, 0.35)" : "rgba(33, 150, 243, 0.35)",
          zIndex: 20,
        },
      }}
    >
      {!compact && (
        <Typography
          noWrap
          sx={{
            position: "absolute",
            top: 3,
            right: 6,
            fontSize: "0.58rem",
            fontWeight: 800,
            lineHeight: 1,
            color: isResidential ? "#b85b00" : "#0b5f89",
          }}
        >
          #{jobId}
        </Typography>
      )}

      <Typography
        noWrap
        sx={{
          fontSize: "0.62rem",
          fontWeight: 800,
          lineHeight: 1.1,
          color: isResidential ? "#b85b00" : "#0b5f89",
          maxWidth: compact ? "100%" : "calc(100% - 42px)",
        }}
      >
        {`${formatCompanyInstant(String(jobEvent.startDate), timeZone, "HH:mm")} - ${formatCompanyInstant(String(jobEvent.endDate), timeZone, "HH:mm")}`}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, width: "100%", minWidth: 0 }}>
        <Typography
          noWrap
          sx={{
            mt: 0.25,
            fontSize: "0.72rem",
            fontWeight: 800,
            lineHeight: 1.15,
            color: "var(--fx-surface-textsecondary, #64748B)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
            flex: 1,
          }}
        >
          {job.alias || (!/^service\s*#/i.test(job.title || "") ? job.title : "") || `Job #${jobId}`}
        </Typography>
      </Box>
    </Box>
  );

  const jobForLayout = {
    ...job,
    id: jobId,
  };

  if (disableJobOpen) {
    return eventCard;
  }

  return (
    <BaseModalComponent
      key={jobEvent.uid || `${job.id}-${jobEvent.startDate}`}
      modalLabel={`Job ${jobId}`}
      modalKey={`Timeline-Event-Stepper-${jobId}-${eventUid}`}
      elementTrigger={eventCard}
    >
      <JobLayout job={jobForLayout} currentEventUid={String(eventUid)} showCalendar={showCalendarInModal} />
    </BaseModalComponent>
  );
}

function EmptyState({ isTodayInTimeZone }: { isTodayInTimeZone: boolean }) {
  return (
    <Box
      sx={{
        position: "absolute",
        left: "50%",
        top: "50%",
        transform: "translate(-50%, -50%)",
        minWidth: "180px",
        height: 34,
        borderRadius: "10px",
        px: 1.5,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "rgba(255, 137, 18, 0.08)",
        border: "1px solid rgba(255, 137, 18, 0.18)",
        boxShadow: "0px 2px 8px rgba(255,137,18,0.08)",
        zIndex: 3,
      }}
    >
      <Typography
        noWrap
        sx={{
          fontSize: "0.72rem",
          fontWeight: 800,
          color: "#ff8912",
        }}
      >
        {isTodayInTimeZone ? "No jobs today" : "No jobs scheduled"}
      </Typography>
    </Box>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export const TodayJobsCalendar = forwardRef<TodayJobsCalendarHandle, TodayJobsCalendarProps>(function TodayJobsCalendar(
  {
    currentDay,
    filter = "all",
    jobs = [],
    isLoading = false,
    disableDateFilter = false,
    highlightedJobId = null,
    compact = false,
    allowMoreMenuInCompact = false,
    moreMenuVariant = "default",
    disableTimelineJobOpen = false,
    disableTimelineScroll = false,
    showCalendarInEventModal = true,
    onJobSelect,
  },
  timelineActionsRef,
) {
  const { ref, isLargeSlot } = useWidgetVariant(500);
  const currentCompany = useAppSelector(selectCurrentCompany);
  const dispatch = useDispatch();
  const lastCreatedJobId = useAppSelector((state: any) => state.jobCreateStepper.lastCreatedJobId);
  const createJobTriggerRef = useRef<HTMLButtonElement | null>(null);
  const pendingClickedTimeRef = useRef<string | null>(null);
  const jobDetailsTriggerRef = useRef<HTMLDivElement | null>(null);
  const createEventTriggerRef = useRef<HTMLDivElement | null>(null);

  const [autoFlowJobId, setAutoFlowJobId] = useState<number | null>(null);
  const [shouldOpenEventModal, setShouldOpenEventModal] = useState(false);

  const autoFlowStartedRef = useRef(false);
  const eventModalOpenedRef = useRef(false);

  const resetAutoFlowLocalState = useCallback(() => {
    setAutoFlowJobId(null);
    setShouldOpenEventModal(false);
    autoFlowStartedRef.current = false;
    eventModalOpenedRef.current = false;
  }, []);

  const resetTimelineAutoFlow = useCallback(() => {
    clearTimelineClickedTime();
    dispatch(clearLastCreatedJobId());
    dispatch(resetJobCreateStepper());
    resetAutoFlowLocalState();
  }, [dispatch, resetAutoFlowLocalState]);

  const { data: allJobsRes } = useJobsAllQuery({ page: 1, pageSize: 999, context: "calendar" });

  const activeJob = useMemo(() => {
    if (!autoFlowJobId) return null;
    return allJobsRes?.data?.items?.find((j: any) => String(j.id) === String(autoFlowJobId));
  }, [allJobsRes, autoFlowJobId]);

  useEffect(() => {
    if (!lastCreatedJobId || !getTimelineClickedTime()) return;

    setAutoFlowJobId(lastCreatedJobId);
    setShouldOpenEventModal(false);
    autoFlowStartedRef.current = false;
    eventModalOpenedRef.current = false;
  }, [lastCreatedJobId]);

  useEffect(() => {
    if (!autoFlowJobId || !getTimelineClickedTime()) return;
    if (autoFlowStartedRef.current || !activeJob) return;

    autoFlowStartedRef.current = true;
    setShouldOpenEventModal(true);
    jobDetailsTriggerRef.current?.click();
  }, [autoFlowJobId, activeJob]);

  useEffect(() => {
    if (!shouldOpenEventModal || !autoFlowJobId || eventModalOpenedRef.current) return;

    let cancelled = false;
    let attempts = 0;

    const tryOpenEventModal = () => {
      if (cancelled || eventModalOpenedRef.current) return;

      if (createEventTriggerRef.current) {
        eventModalOpenedRef.current = true;
        createEventTriggerRef.current.click();
        return;
      }

      if (attempts >= 40) return;

      attempts += 1;
      window.setTimeout(tryOpenEventModal, 50);
    };

    tryOpenEventModal();

    return () => {
      cancelled = true;
    };
  }, [shouldOpenEventModal, autoFlowJobId]);

  const timeZone = currentCompany?.timeZone || "UTC";
  const [now, setNow] = useState(() => new Date());
  const [closeMoreJobsPopoverKey, setCloseMoreJobsPopoverKey] = useState(0);
  const [openMoreGroupKey, setOpenMoreGroupKey] = useState<string | null>(null);
  const [dismissedHighlight, setDismissedHighlight] = useState(false);
  const activeHighlightedJobId = dismissedHighlight ? null : highlightedJobId;
  const timelineCalendarHeight = TIMELINE.CALENDAR_HEIGHT;
  const timelineGridHeight = TIMELINE.CALENDAR_HEIGHT - 24;
  const timelineWidth = isLargeSlot ? 1650 : 1200;
  const drag = useDragScroll();
  const autoScrolledToNowRef = useRef<string | null>(null);

  useEffect(() => {
    setDismissedHighlight(false);
  }, [highlightedJobId]);

  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), 30_000);
    return () => window.clearInterval(interval);
  }, []);

  const dateToUse = useMemo(() => (currentDay ? dayjs(currentDay) : dayjs()), [currentDay]);

  const isTodayInTimeZone = useMemo(() => {
    const todayStr = formatDate(now, timeZone, "yyyy-MM-dd");
    const targetStr = formatDate(dateToUse.toDate(), timeZone, "yyyy-MM-dd");

    return todayStr === targetStr;
  }, [dateToUse, timeZone, now]);

  const currentMinutesInTimeZone = useMemo(() => {
    const hour = parseInt(formatDate(now, timeZone, "H") || "0", 10);
    const minute = parseInt(formatDate(now, timeZone, "m") || "0", 10);

    return (hour - TIMELINE.START_HOUR) * 60 + minute;
  }, [timeZone, now]);

  const isCurrentTimeInTimeline = currentMinutesInTimeZone >= 0 && currentMinutesInTimeZone <= TIMELINE.TOTAL_MINUTES;

  const scrollTimelineToMinutes = useCallback(
    (targetMinutes: number) => {
      const scrollContainer = drag.scrollRef.current;
      if (!scrollContainer) return;

      const clampedMinutes = Math.max(0, Math.min(TIMELINE.TOTAL_MINUTES, targetMinutes));
      const timelineContentWidth = timelineWidth - TIMELINE.EDGE_PADDING * 2;
      const targetX = TIMELINE.EDGE_PADDING + (clampedMinutes / TIMELINE.TOTAL_MINUTES) * timelineContentWidth + TIMELINE.EXPANDED_WIDTH_PX / 2;
      const maxScrollLeft = Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth);
      const scrollLeft = Math.max(0, Math.min(maxScrollLeft, targetX - scrollContainer.clientWidth / 2));

      scrollContainer.scrollTo({ left: scrollLeft, behavior: "smooth" });
    },
    [drag.scrollRef, timelineWidth],
  );

  const scrollToDate = useCallback(
    (date: string | Date) => {
      const targetDate = new Date(date);
      if (Number.isNaN(targetDate.getTime())) return;

      const hour = parseInt(formatDate(targetDate, timeZone, "H") || "0", 10);
      const minute = parseInt(formatDate(targetDate, timeZone, "m") || "0", 10);
      scrollTimelineToMinutes((hour - TIMELINE.START_HOUR) * 60 + minute);
    },
    [scrollTimelineToMinutes, timeZone],
  );

  useImperativeHandle(timelineActionsRef, () => ({ scrollToDate }), [scrollToDate]);

  useEffect(() => {
    const scrollContainer = drag.scrollRef.current;
    if (!scrollContainer || !isTodayInTimeZone || !isCurrentTimeInTimeline) return;

    const currentDayKey = formatDate(dateToUse.toDate(), timeZone, "yyyy-MM-dd");
    const scrollKey = `${currentDayKey}-${timeZone}-${timelineWidth}`;
    if (autoScrolledToNowRef.current === scrollKey) return;

    autoScrolledToNowRef.current = scrollKey;

    const timelineContentWidth = timelineWidth - TIMELINE.EDGE_PADDING * 2;
    const currentTimeX = TIMELINE.EDGE_PADDING + (currentMinutesInTimeZone / TIMELINE.TOTAL_MINUTES) * timelineContentWidth;
    const maxScrollLeft = Math.max(0, scrollContainer.scrollWidth - scrollContainer.clientWidth);
    const scrollLeft = Math.max(0, Math.min(maxScrollLeft, currentTimeX - scrollContainer.clientWidth / 2));

    window.requestAnimationFrame(() => {
      scrollContainer.scrollTo({ left: scrollLeft, behavior: "smooth" });
    });
  }, [currentMinutesInTimeZone, dateToUse, drag.scrollRef, isCurrentTimeInTimeline, isTodayInTimeZone, timeZone, timelineWidth]);

  const dayEvents = useMemo(() => {
    const currentFilter = filter.toLowerCase();
    const targetDayStr = formatDate(dateToUse.toDate(), timeZone, "yyyy-MM-dd");

    return jobs
      .filter((job) => currentFilter === "all" || job.type?.toLowerCase() === currentFilter)
      .filter((job) => {
        if (!job.startDate || !job.endDate) return false;

        if (disableDateFilter) return true;

        const eventDayStr = formatDate(job.startDate, timeZone, "yyyy-MM-dd");

        return eventDayStr === targetDayStr;
      })
      .map((job) => ({
        job,
        jobEvent: {
          uid: job.eventUid || job.uid || job.id,
          startDate: job.startDate,
          endDate: job.endDate,
        },
      }));
  }, [dateToUse, jobs, filter, timeZone, disableDateFilter]);

  const repeatedHourEvents = useMemo(() => dayEvents
    .filter(({ jobEvent }) => isRepeatedCompanyInstant(String(jobEvent.startDate), timeZone))
    .sort((a, b) => new Date(a.jobEvent.startDate).getTime() - new Date(b.jobEvent.startDate).getTime()),
  [dayEvents, timeZone]);

  useEffect(() => {
    if (!activeHighlightedJobId) return;

    const highlightedEvent = dayEvents.find(({ job }) => jobMatchesId(job, activeHighlightedJobId));
    if (!highlightedEvent?.jobEvent?.startDate) return;

    window.requestAnimationFrame(() => {
      scrollToDate(highlightedEvent.jobEvent.startDate);
    });
  }, [activeHighlightedJobId, dayEvents, scrollToDate]);

  // Agrupa los eventos que se enciman en el calendario para mostrar solo una card y mandar los demas al popover de "more".
  const groupedEvents = useMemo(() => groupOverlappingJobs(dayEvents), [dayEvents]);

  const timelineContextMenu = useMemo(
    () =>
      buildTimelineContextMenu({
        onCreateJob: () => {
          dispatch(clearLastCreatedJobId());
          dispatch(resetJobCreateStepper());
          resetAutoFlowLocalState();

          if (pendingClickedTimeRef.current) {
            localStorage.setItem(TIMELINE_CLICKED_TIME_KEY, pendingClickedTimeRef.current);
            pendingClickedTimeRef.current = null;
          }

          createJobTriggerRef.current?.click();
        },
        onChangeFilter: () => {},
      }),
    [dispatch, resetAutoFlowLocalState],
  );

  const timelineContext = useMemo<TimelineContextMenuContext>(
    () => ({
      currentDay: dateToUse.toDate(),
      timeZone,
      filter,
      jobsCount: dayEvents.length,
      isTodayInTimeZone,
    }),
    [dateToUse, timeZone, filter, dayEvents.length, isTodayInTimeZone],
  );

  const captureTimelineCoordinate = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const scrollContainer = drag.scrollRef.current;
      if (!scrollContainer) return;

      const rect = scrollContainer.getBoundingClientRect();
      const clickX = e.clientX - rect.left + scrollContainer.scrollLeft - TIMELINE.EDGE_PADDING;
      const totalWidth = timelineWidth - TIMELINE.EDGE_PADDING * 2;
      const ratio = Math.max(0, Math.min(1, clickX / totalWidth));
      const snappedMinutes = Math.round((ratio * TIMELINE.TOTAL_MINUTES) / 15) * 15;

      const clickedDate = dateToUse.toDate();
      clickedDate.setHours(TIMELINE.START_HOUR + Math.floor(snappedMinutes / 60), snappedMinutes % 60, 0, 0);

      pendingClickedTimeRef.current = clickedDate.toISOString();
    },
    [dateToUse, timelineWidth, drag.scrollRef],
  );

  if (isLoading) {
    return <Skeleton variant="rectangular" height={150} sx={{ borderRadius: "16px" }} />;
  }

  return (
    <>
      <Paper
        ref={ref}
        elevation={0}
        sx={{
          width: "100%",
          p: "16px 24px",
          backgroundColor: "var(--fx-surface-base, #FFFFFF)",
          borderRadius: "16px",
          minHeight: "150px",
          boxSizing: "border-box",
        }}
      >
        {repeatedHourEvents.length > 0 && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700 }}>
              Repeated clock hour · exact visit order
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {repeatedHourEvents.map(({ job, jobEvent }) => {
                const eventUid = jobEvent.uid || job.eventUid || job.uid || job.id;
                const jobId = job.jobId || job.serviceOrderId || job.id;
                const label = `${formatCompanyInstant(String(jobEvent.startDate), timeZone)} · ${job.alias || job.title || `Job #${jobId}`}`;
                const trigger = <Button size="small" variant="outlined" onClick={disableTimelineJobOpen ? () => onJobSelect?.(job) : undefined}>{label}</Button>;
                return disableTimelineJobOpen ? (
                  <Box key={String(eventUid)}>{trigger}</Box>
                ) : (
                  <BaseModalComponent
                    key={String(eventUid)}
                    modalKey={`Timeline-Repeated-Event-${jobId}-${eventUid}`}
                    modalLabel={label}
                    elementTrigger={trigger}
                  >
                    <JobLayout job={{ ...job, id: jobId }} currentEventUid={String(eventUid)} showCalendar={showCalendarInEventModal} />
                  </BaseModalComponent>
                );
              })}
            </Box>
          </Box>
        )}
        <Box onContextMenu={captureTimelineCoordinate}>
          <ContextMenuTrigger menu={timelineContextMenu} context={timelineContext}>
            <Box
              ref={drag.scrollRef}
              onMouseDown={(event) => {
                setDismissedHighlight(true);
                setOpenMoreGroupKey(null);
                setCloseMoreJobsPopoverKey((prev) => prev + 1);
                drag.onMouseDown(event);
              }}
              onMouseMove={drag.onMouseMove}
              onMouseUp={drag.onMouseUp}
              onMouseLeave={drag.onMouseLeave}
              //onContextMenu={captureTimelineCoordinate}
              sx={{
                overflowX: "auto",
                overflowY: disableTimelineScroll ? "hidden" : moreMenuVariant === "advanced" ? "visible" : "hidden",
                maxHeight: timelineCalendarHeight,
                cursor: drag.isDragging ? "grabbing" : "grab",
                userSelect: "none",
                pb: 0.5,
                scrollbarWidth: disableTimelineScroll ? "none" : "thin",
                "&::-webkit-scrollbar": { height: disableTimelineScroll ? 0 : "5px", width: disableTimelineScroll ? 0 : undefined },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
                  borderRadius: "10px",
                },
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: timelineCalendarHeight,
                  mt: 2,
                  width: `${timelineWidth}px`,
                  minWidth: `${timelineWidth}px`,
                  px: `${TIMELINE.EDGE_PADDING}px`,
                  boxSizing: "border-box",
                  zIndex: moreMenuVariant === "advanced" ? 2 : 1,
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: timelineGridHeight,
                  }}
                >
                  <TimeGrid />

                  <Box
                    sx={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    {/*
                    Aqui se renderizaban los eventos del dia uno por uno.
                    Se deja comentado como referencia porque ahora se usaran grupos para evitar que las cards se encimen.

                    {dayEvents.length === 0 ? (
                      <EmptyState isTodayInTimeZone={isTodayInTimeZone} />
                    ) : (
                      // Aqui se renderizaban normalmente los eventos.
                      dayEvents.map(({ job, jobEvent }, index) => (
                        <EventCard
                          key={jobEvent.uid || `${job.id}-${jobEvent.startDate}`}
                          job={job}
                          jobEvent={jobEvent}
                          index={index}
                          timeZone={timeZone}
                        />
                      ))
                    )} */}
                    {/* Aqui se renderizan los eventos del dia por grupos */}
                    {groupedEvents.map((group, index) => {
                      const firstEvent = group[0];
                      const layout = getEventLayout(firstEvent.jobEvent.startDate, firstEvent.jobEvent.endDate, timeZone);
                      const staggerOffset = (index % 3) * TIMELINE.STAGGER_Y;
                      const groupKey = String(firstEvent.jobEvent.uid || `${firstEvent.job.id}-${firstEvent.jobEvent.startDate}`);
                      const isHighlightedGroup = group.some(({ job }) => jobMatchesId(job, activeHighlightedJobId));
                      const shouldOpenMoreForHighlightedJob = group.slice(1).some(({ job }) => jobMatchesId(job, activeHighlightedJobId));
                      if (!layout) return null;

                      return (
                        <Box key={groupKey} sx={{ display: "contents" }}>
                          <EventCard
                            job={firstEvent.job}
                            jobEvent={firstEvent.jobEvent}
                            index={index}
                            timeZone={timeZone}
                            forceExpanded={openMoreGroupKey === groupKey || isHighlightedGroup}
                            compact={compact}
                            disableTransition={moreMenuVariant === "advanced"}
                            disableJobOpen={disableTimelineJobOpen}
                            showCalendarInModal={showCalendarInEventModal}
                            onJobSelect={onJobSelect}
                          />

                          {/* Si el grupo tiene mas de un evento, mostramos el popover con los jobs que quedaron ocultos. */}
                          {group.length > 1 && (
                            <Box
                              sx={{
                                position: "absolute",
                                left: `${layout.left}%`,
                                top: `calc(50% + ${staggerOffset - 48}px)`,
                                zIndex: 30,
                              }}
                            >
                              <MoreJobsPopover
                                jobs={group.slice(1).map(({ job }) => job)}
                                timeZone={timeZone}
                                closeKey={closeMoreJobsPopoverKey}
                                onSelectJob={onJobSelect || (() => {})}
                                forceOpen={moreMenuVariant === "advanced" ? false : shouldOpenMoreForHighlightedJob}
                                highlightedJobId={activeHighlightedJobId}
                                disableMenu={compact && !allowMoreMenuInCompact}
                                variant={moreMenuVariant}
                                disableJobOpen={disableTimelineJobOpen}
                                showCalendarInModal={showCalendarInEventModal}
                                onOpenChange={(isOpen) => {
                                  setOpenMoreGroupKey((currentGroupKey) => {
                                    if (isOpen) return groupKey;
                                    return currentGroupKey === groupKey ? null : currentGroupKey;
                                  });
                                }}
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}

                    {isTodayInTimeZone && isCurrentTimeInTimeline && <NowIndicator currentMinutes={currentMinutesInTimeZone} />}
                  </Box>
                </Box>
              </Box>
            </Box>
          </ContextMenuTrigger>
        </Box>
        <CompanyTimeCaption />
      </Paper>

      <BaseModalComponent
        modalKey="Timeline-JobsAdd-Business-Without-Quote"
        modalLabel="Add New Job"
        onConfirmClose={resetTimelineAutoFlow}
        elementTrigger={
          <button ref={createJobTriggerRef} type="button" style={{ display: "none" }} data-modal-target="Timeline-JobsAdd-Business-Without-Quote" />
        }
      >
        <JobsCreate />
      </BaseModalComponent>

      {autoFlowJobId && activeJob && (
        <JobDetModal
          job={activeJob}
          trigger={<Box ref={jobDetailsTriggerRef} sx={{ display: "none" }} />}
          modalKey={`Timeline-JobDetails-${autoFlowJobId}`}
          wrapTrigger={false}
        />
      )}

      {shouldOpenEventModal && autoFlowJobId && (
        <BaseModalComponent
          modalKey={`Timeline-Job-Open-New-Event-${autoFlowJobId}`}
          modalLabel="Create Event"
          elementTrigger={<Box ref={createEventTriggerRef} sx={{ display: "none" }} />}
          onClose={resetTimelineAutoFlow}
        >
          <VisitStepper jobId={autoFlowJobId} closeModalKey={`Timeline-Job-Open-New-Event-${autoFlowJobId}`} onComplete={resetAutoFlowLocalState} />
        </BaseModalComponent>
      )}
    </>
  );
});
